import { useListExchangeRates, useSyncExchangeRates } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import { ColumnHeader, EmptyState, FilterField, TableShell } from '@modules/Admin/Reporting/common';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useState } from 'react';

const STALE_AFTER_DAYS = 4;
const CURRENCIES = ['EUR', 'USD', 'GBP'] as const;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 last:border-b-0';

const MONTH_OPTIONS = moment.months().map((label, index) => ({ label, value: index + 1 }));

const formatRate = (value?: number) => (typeof value === 'number' ? value.toFixed(4) : '—');

const ExchangeRatesTab = () => {
  const toast = useToast();
  const [year, setYear] = useState<number | undefined>(moment().year());
  const [month, setMonth] = useState<number | undefined>();

  const { data, isLoading, isFetching } = useListExchangeRates(
    { year, month: year ? month : undefined },
    { keepPreviousData: true }
  );

  const { mutate: sync, isLoading: isSyncing } = useSyncExchangeRates({
    onSuccess: (result) => {
      toast.success(
        result.added
          ? `Saved ${result.added} new exchange rates from BNR.`
          : 'Everything was already up to date.'
      );
    },
    onError: (error) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(message ?? 'Exchange rates could not be fetched from BNR.');
    },
  });

  const latestDate = data?.latestDate;
  const daysOld = latestDate ? moment().startOf('day').diff(moment(latestDate), 'days') : undefined;
  const isStale = daysOld === undefined || daysOld > STALE_AFTER_DAYS;

  const yearOptions = [...new Set([moment().year(), ...(data?.years ?? [])])]
    .sort((a, b) => b - a)
    .map((value) => ({ label: String(value), value }));

  return (
    <div className="flex w-full flex-col gap-6 py-6 font-raleway text-white">
      <div
        className={mergeClassNames(
          'flex flex-wrap items-center justify-between gap-4 rounded-2xl p-5',
          isStale ? 'bg-red-500/10' : 'bg-surfaceObject'
        )}
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm text-customGrey">Latest saved BNR rate</span>
          <span className={mergeClassNames('text-2xl font-bold', isStale ? 'text-red-300' : '')}>
            {latestDate ? moment(latestDate).format('DD MMM YYYY') : 'None'}
          </span>
          {isStale ? (
            <span className="max-w-2xl text-sm text-red-300">
              No new rates were saved for {daysOld ?? 'many'} days. Conversions keep using the last
              saved rate until new ones are fetched.
            </span>
          ) : (
            <span className="text-sm text-customGrey">
              Rates are fetched from BNR every night; weekends and bank holidays have no rate.
            </span>
          )}
        </div>
        <Button
          buttonText="Fetch missing rates from BNR"
          isLoading={isSyncing}
          disabled={isSyncing}
          onClick={() => sync({})}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-36">
          <Select
            variant="secondary"
            label="Year"
            placeholder="All years"
            value={year}
            options={yearOptions}
            onChange={(value) => {
              setYear((value as number) ?? undefined);
              setMonth(undefined);
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-44">
          <Select
            variant="secondary"
            label="Month"
            placeholder="All months"
            isDisabled={!year}
            value={month}
            options={MONTH_OPTIONS}
            onChange={(value) => setMonth((value as number) ?? undefined)}
          />
        </FilterField>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : !data?.rows.length ? (
        <EmptyState>No exchange rates saved for this period.</EmptyState>
      ) : (
        <div className={isFetching ? 'opacity-60' : ''}>
          <TableShell minWidth="min-w-[480px]">
            <thead>
              <tr className={headRow}>
                <th className="px-5 py-3 text-left font-semibold">Date</th>
                {CURRENCIES.map((currency) => (
                  <ColumnHeader key={currency}>1 {currency} in RON</ColumnHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.date} className={bodyRow}>
                  <td className="px-5 py-2.5 text-left text-customLight">
                    {moment(row.date).format('ddd, DD MMM YYYY')}
                  </td>
                  {CURRENCIES.map((currency) => (
                    <td key={currency} className="px-5 py-2.5 text-right tabular-nums">
                      {formatRate(row[currency])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </TableShell>
          <p className="mt-2 text-xs text-customGrey">
            {data.rows.length} days shown. Source: National Bank of Romania reference rates.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExchangeRatesTab;
